/**
 * Where to send someone after they authenticate.
 *
 * The value comes from a query string, so it is attacker-controlled and ends
 * up in `window.location.href` — an unvalidated one is an open redirect, and
 * a protocol-relative `//evil.com` is the shape that slips past a naive
 * "starts with /" check. Only a same-origin absolute PATH is accepted.
 */
const PARAM = 'returnTo';

export function safeReturnTo(search: string = window.location.search): string {
  try {
    const raw = new URLSearchParams(search).get(PARAM);
    if (!raw) return '/';
    // Reject anything with a scheme or an authority: `//host`, `https://host`,
    // `\\host` (which some browsers normalize to `//host`), and any control
    // character that could be used to smuggle one past this check.
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return '/';
    // Control characters, checked by code point rather than a regex: browsers
    // strip some of them while normalizing, so `/\n/evil.com` can become
    // `//evil.com` after this check would have passed it. (A control-character
    // character class trips `no-control-regex`, and suppressing a lint rule
    // reads as an exception where this is just the plainer way to write it.)
    for (let i = 0; i < raw.length; i++) {
      const code = raw.charCodeAt(i);
      if (code <= 0x1f || code === 0x7f) return '/';
    }
    return raw;
  } catch {
    return '/';
  }
}

/** Build a login URL that comes back to where the user was. */
export function loginUrlReturningTo(path: string): string {
  return `/login?${PARAM}=${encodeURIComponent(path)}`;
}
